import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "src/context/AuthContext";
import { useBookingStore } from "src/store/bookingStore";
import TutorAvailabilityViewer from "src/components/student/scheduling/TutorAvailabilityViewer";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { Textarea } from "src/components/ui/TextArea";
import { Calendar } from "src/components/ui/Calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/Select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/Dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/ui/Form";
import { RadioGroup, RadioGroupItem } from "src/components/ui/RadioGroup";
import { Label } from "src/components/ui/Label";
import { Badge } from "src/components/ui/Badge";
import { Checkbox } from "src/components/ui/Checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";
import { useToast } from "src/components/ui/UseToast";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  BookOpen,
  Calendar as CalendarIcon,
  Clock,
  FileText,
  Info,
  User,
  Users,
  AlertCircle,
  RefreshCw,
  X,
} from "lucide-react";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { studentBookingCurriculumService, type BookingDomain, type BookingObjective, type BookingUnit } from "src/services/studentBookingCurriculumService";



// Form schema
const formSchema = z.object({
  batch: z.string({
    required_error: "Please select an enrollment",
  }),
  domain: z.string({ required_error: "Please select a domain" }),
  units: z.array(z.string()).min(1, "Please select at least one unit"),
  objectives: z.array(z.string()).default([]),
  tutor: z.string({
    required_error: "Please select a tutor",
  }),
  tutorChangeReason: z.string().optional(),
  notes: z.string().optional(),
});

const RequestBooking: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [showTutorChangeDialog, setShowTutorChangeDialog] = useState(false);
  const [tutorChangeReason, setTutorChangeReason] = useState("");
  const { setTitle, setDescription, reset } = usePageHeaderStore();

  // Get state from Zustand store
  const {
    batches,
    tutors,
    selectedBatch,
    selectedUnit,
    selectedObjective,
    assignedTutor,
    selectedTutor,
    isLoading,
    error,
    selectionComplete,
    showChangeTutor,
    setSelectedTutor,
    setSelectionComplete,
    setShowChangeTutor,
    fetchStudentBatches,
    loadAlternativeTutors,
    submitTutorChangeRequest,
  } = useBookingStore();
  const [domains, setDomains] = useState<BookingDomain[]>([]);
  const [units, setUnits] = useState<BookingUnit[]>([]);
  const [objectives, setObjectives] = useState<BookingObjective[]>([]);
  const [isLoadingCurriculum, setIsLoadingCurriculum] = useState(true);
  const [curriculumError, setCurriculumError] = useState<string | null>(null);

  // Initialize form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      batch: "",
      domain: "",
      units: [],
      objectives: [],
      tutor: "",
      tutorChangeReason: "",
      notes: "",
    },
  });
  const selectedEnrollment = form.watch("batch");
  const selectedDomainId = form.watch("domain");
  const selectedUnitIds = form.watch("units");
  const selectedObjectiveIds = form.watch("objectives");

  // Load student batches on component mount
  useEffect(() => {
    fetchStudentBatches();
  }, [fetchStudentBatches]);

  useEffect(() => {
    let isCurrent = true;
    setDomains([]); setUnits([]); setObjectives([]); setCurriculumError(null);
    if (!selectedEnrollment) { setIsLoadingCurriculum(false); return () => { isCurrent = false; }; }
    setIsLoadingCurriculum(true);
    studentBookingCurriculumService.listDomains(selectedEnrollment)
      .then((data) => { if (isCurrent) setDomains(data); })
      .catch((loadError) => { if (isCurrent) setCurriculumError(loadError instanceof Error ? loadError.message : "Unable to load curriculum."); })
      .finally(() => { if (isCurrent) setIsLoadingCurriculum(false); });
    return () => { isCurrent = false; };
  }, [selectedEnrollment]);

  // Set up page header
  useEffect(() => {
    setTitle("Request Booking");
    setDescription("Request a tutoring session for a domain, unit, and learning objectives");
    return () => reset();
  }, [setTitle, setDescription, reset]);

  // Preserve an enrollment supplied from the Learning Journey page.
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const batchParam = searchParams.get('batch');

    if (batches.length > 0) {
      // If batch is specified in URL, select it
      if (batchParam) {
        const batch = batches.find(b => b.name === batchParam || b.id === batchParam);
        if (batch) {
          useBookingStore.setState({ selectedBatch: batch.id });
          form.setValue("batch", batch.id);
        }
      }
    }
  }, [location.search, batches.length, batches, form]);

  // Update form when assigned tutor changes
  useEffect(() => {
    if (assignedTutor) {
      form.setValue("tutor", assignedTutor.id);
    }
  }, [assignedTutor, form]);

  // State to track if a tutor change request has been submitted
  const [tutorChangeRequestSubmitted, setTutorChangeRequestSubmitted] = useState(false);
  const isRequestReady = Boolean(selectedEnrollment && selectedDomainId && selectedUnitIds.length && assignedTutor && selectedTutor);

  // Handle form submission
  const onSubmit = (data: z.infer<typeof formSchema>) => {
    // In a real app, you would submit the form data to an API
    console.log("Form submitted:", data);

    // Get the selected learning path objects for better logging.
    const selectedBatchObj = batches.find(b => b.id === data.batch);
    const selectedDomain = domains.find((domain) => domain.id === data.domain);
    const selectedUnits = units.filter((unit) => data.units.includes(unit.id));
    const selectedObjectives = objectives.filter((objective) => data.objectives.includes(objective.id));

    console.log("Selected batch:", selectedBatchObj?.name);
    console.log("Selected domain:", selectedDomain?.name);
    console.log("Selected units:", selectedUnits.map((unit) => unit.name));
    console.log("Selected learning objectives:", selectedObjectives.map((objective) => objective.title));

    // If the tutor change request notice is already shown, don't do anything
    // This is because the request was already submitted when the user clicked "Confirm Change"
    if (tutorChangeRequestSubmitted) {
      return;
    }

    // Note: We're not handling regular session requests here anymore
    // as they are now handled by the TutorAvailabilityViewer component
    // This form submission is only used for tutor change requests
  };

  // Handle tutor change
  const handleTutorChange = (tutorId: string) => {
    const isAssigned = assignedTutor?.id === tutorId;

    if (!isAssigned) {
      setShowTutorChangeDialog(true);
      setSelectedTutor(tutorId);
    } else {
      setSelectedTutor(tutorId);
      form.setValue("tutor", tutorId);
      form.setValue("tutorChangeReason", "");
    }
  };

  // Confirm tutor change with reason
  const confirmTutorChange = async () => {
    if (selectedTutor && tutorChangeReason.trim()) {
      try {
        // Submit the tutor change request
        await submitTutorChangeRequest(tutorChangeReason);

        // Mark the selected tutor as requested
        const updatedTutors = tutors.map(tutor =>
          tutor.id === selectedTutor
            ? { ...tutor, isRequested: true }
            : tutor
        );

        // Update the store with the updated tutors
        useBookingStore.setState({ tutors: updatedTutors });

        form.setValue("tutor", selectedTutor);
        form.setValue("tutorChangeReason", tutorChangeReason);
        setShowTutorChangeDialog(false);

        // Show the tutor change request notice immediately
        setTutorChangeRequestSubmitted(true);

        // Show a toast notification
        toast({
          title: "Tutor change request submitted",
          description: "Your request has been sent to the admin for approval.",
        });
      } catch (error) {
        console.error('Error submitting tutor change request:', error);
        toast({
          title: "Error",
          description: "Failed to submit tutor change request. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rfpurple-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-8 bg-red-50 rounded-lg">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600">{error}</p>
          <Button
            variant="outline"
            className="mt-4"
            onClick={() => fetchStudentBatches()}
          >
            Try Again
          </Button>
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 pb-8">
            <Card>
              <CardHeader>
                <CardTitle>Learning Path Selection</CardTitle>
                <CardDescription>
                  Choose the domain and unit for this session. You can also add one or more learning objectives.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(19rem,0.7fr)]">
                <div className="space-y-4">
                {selectionComplete ? (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <h3 className="font-medium">Selected Learning Path</h3>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectionComplete(false)}
                        className="text-xs"
                      >
                        Edit
                      </Button>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <p className="text-gray-500">Domain</p>
                        <p className="font-medium">{domains.find((domain) => domain.id === form.getValues("domain"))?.name}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Unit</p>
                        <p className="font-medium">{selectedUnitIds.length} selected</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Learning objectives</p>
                        <p className="font-medium">{selectedObjectiveIds.length || "No"} selected</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <FormField
                      control={form.control}
                      name="batch"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Enrollment <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only"> (required)</span></FormLabel>
                          <Select onValueChange={(value) => { field.onChange(value); useBookingStore.setState({ selectedBatch: value, assignedTutor: null, selectedTutor: null }); }} value={field.value} disabled={!batches.length}>
                            <FormControl><SelectTrigger><SelectValue placeholder={batches.length ? "Select an enrollment" : "No active enrollments available"} /></SelectTrigger></FormControl>
                            <SelectContent>{batches.map((batch) => <SelectItem key={batch.id} value={batch.id}>{batch.name}</SelectItem>)}</SelectContent>
                          </Select>
                          <FormMessage />
                          {!batches.length && (
                            <div className="mt-3 flex flex-col gap-3 rounded-lg border border-violet-200 bg-violet-50/60 p-4 sm:flex-row sm:items-center sm:justify-between">
                              <div className="flex gap-3">
                                <Info className="mt-0.5 h-5 w-5 shrink-0 text-violet-700" aria-hidden="true" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900">You don’t have an active enrollment yet.</p>
                                  <p className="mt-1 text-sm text-gray-600">Choose a learning plan to unlock session booking.</p>
                                </div>
                              </div>
                              <Button type="button" variant="outline" onClick={() => navigate("/student/products")}>Explore plans</Button>
                            </div>
                          )}
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="domain"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Domain <span className="text-red-600" aria-hidden="true">*</span>
                            <span className="sr-only"> (required)</span>
                          </FormLabel>
                          <Select
                            onValueChange={async (value) => {
                              field.onChange(value);
                              form.setValue("units", []);
                              form.setValue("objectives", []);
                              setObjectives([]);
                              try { setUnits(await studentBookingCurriculumService.listUnits(selectedEnrollment, value)); }
                              catch (loadError) { setCurriculumError(loadError instanceof Error ? loadError.message : "Unable to load units."); }
                            }}
                            value={field.value}
                            disabled={isLoadingCurriculum || !selectedEnrollment}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select a domain" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {domains.map((domain) => (
                                <SelectItem key={domain.id} value={domain.id}>
                                  {domain.name}
                                </SelectItem>
                                ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Unit Selection */}
                    <FormField
                      control={form.control}
                      name="units"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>
                            Unit <span className="text-red-600" aria-hidden="true">*</span>
                            <span className="sr-only"> (required)</span>
                          </FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button type="button" variant="outline" disabled={!form.getValues("domain")} className="h-11 w-full justify-between bg-background font-normal hover:bg-muted/50">
                                  {field.value.length ? `${field.value.length} unit${field.value.length === 1 ? "" : "s"} selected` : form.getValues("domain") ? "Select units" : "Select a domain first"}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-2">
                              <div className="max-h-64 space-y-1 overflow-y-auto">
                                {units.map((unit) => {
                                  const checked = field.value.includes(unit.id);
                                  return <label key={unit.id} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-muted"><Checkbox checked={checked} onCheckedChange={async (isChecked) => {
                                    const next = isChecked ? [...field.value, unit.id] : field.value.filter((id) => id !== unit.id);
                                    field.onChange(next);
                                    if (!next.length) { form.setValue("objectives", []); setObjectives([]); useBookingStore.setState({ selectedUnit: null, selectedObjective: null, assignedTutor: null, selectedTutor: null }); return; }
                                    try {
                                      const objectiveGroups = await Promise.all(next.map(async (unitId) => (await studentBookingCurriculumService.listObjectives(selectedEnrollment, unitId)).map((objective) => ({ ...objective, unitId }))));
                                      const nextObjectives = objectiveGroups.flat();
                                      setObjectives(nextObjectives);
                                      form.setValue("objectives", form.getValues("objectives").filter((objectiveId) => nextObjectives.some((objective) => objective.id === objectiveId)));
                                      useBookingStore.setState({ selectedUnit: next[0], selectedObjective: null, assignedTutor: null, selectedTutor: null });
                                      if (selectedBatch) await useBookingStore.getState().getAssignedTutor(selectedBatch, next[0]);
                                    } catch (loadError) { setCurriculumError(loadError instanceof Error ? loadError.message : "Unable to load learning objectives."); }
                                  }} /><span>{unit.name}</span></label>;
                                })}
                                {!units.length && <p className="px-2 py-3 text-sm text-muted-foreground">No published units are available for this domain.</p>}
                              </div>
                            </PopoverContent>
                          </Popover>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {/* Learning Objective Selection */}
                    <FormField
                      control={form.control}
                      name="objectives"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Learning objectives <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
                          <Popover>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <Button
                                  type="button"
                                  variant="outline"
                                  disabled={!selectedUnitIds.length}
                                  className="h-11 w-full justify-between bg-background font-normal hover:bg-muted/50"
                                >
                                  {field.value.length
                                    ? `${field.value.length} objective${field.value.length === 1 ? "" : "s"} selected`
                                    : selectedUnitIds.length ? "Select learning objectives" : "Select at least one unit first"}
                                </Button>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-2">
                              <div className="max-h-64 space-y-1 overflow-y-auto">
                                {objectives.map((objective) => {
                                  const checked = field.value.includes(objective.id);
                                  return (
                                    <label key={objective.id} className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 text-sm hover:bg-muted">
                                      <Checkbox
                                        checked={checked}
                                        onCheckedChange={(isChecked) => {
                                          const next = isChecked
                                            ? [...field.value, objective.id]
                                            : field.value.filter((id) => id !== objective.id);
                                          field.onChange(next);
                                          useBookingStore.setState({ selectedObjective: next[0] || null });
                                        }}
                                      />
                                      <span>{objective.title}</span>
                                    </label>
                                  );
                                })}
                                {!objectives.length && <p className="px-2 py-3 text-sm text-muted-foreground">No published learning objectives are available for this unit.</p>}
                              </div>
                            </PopoverContent>
                          </Popover>
                          <FormDescription>Choose any objectives you would like to cover.</FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
                {curriculumError && <p className="text-sm text-destructive" role="alert">{curriculumError}</p>}
                </div>

                <aside className="rounded-xl border border-violet-100 bg-violet-50/40 p-5 lg:sticky lg:top-6 lg:self-start" aria-label="Selected learning map">
                  <div className="mb-5 flex items-center gap-2">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-100 text-violet-700"><BookOpen className="h-4 w-4" /></div>
                    <div><h3 className="font-semibold text-gray-900">Learning map</h3><p className="text-xs text-gray-600">Your selected path</p></div>
                  </div>
                  {selectedUnitIds.length ? (
                    <div className="space-y-4">
                      <div className="rounded-lg border border-violet-200 bg-white px-3 py-2">
                        <p className="text-xs font-medium uppercase tracking-wide text-violet-700">Domain</p>
                        <p className="mt-1 text-sm font-medium text-gray-900">{domains.find((domain) => domain.id === form.getValues("domain"))?.name || "Choose a domain"}</p>
                      </div>
                      <div className="space-y-3 border-l-2 border-violet-200 pl-4">
                        {units.filter((unit) => selectedUnitIds.includes(unit.id)).map((unit) => {
                          const unitObjectives = objectives.filter((objective) => objective.unitId === unit.id);
                          const chosenObjectives = unitObjectives.filter((objective) => selectedObjectiveIds.includes(objective.id));
                          return <div key={unit.id} className="relative"><span className="absolute -left-[1.42rem] top-2 h-3 w-3 rounded-full border-2 border-violet-300 bg-white" /><p className="text-sm font-medium text-gray-900">{unit.name}</p>{chosenObjectives.length ? <ul className="mt-2 space-y-1.5">{chosenObjectives.map((objective) => <li key={objective.id} className="flex gap-2 text-xs leading-5 text-gray-700"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />{objective.title}</li>)}</ul> : <p className="mt-1 text-xs text-gray-500">No objectives selected</p>}</div>;
                        })}
                      </div>
                    </div>
                  ) : <div className="rounded-lg border border-dashed border-violet-200 bg-white/70 px-4 py-8 text-center"><BookOpen className="mx-auto h-5 w-5 text-violet-400" /><p className="mt-2 text-sm font-medium text-gray-700">Your path will appear here</p><p className="mt-1 text-xs leading-5 text-gray-500">Select one or more units to build your learning map.</p></div>}
                </aside>
              </CardContent>
            </Card>

            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
              {!isRequestReady && <p className="text-sm text-muted-foreground sm:mr-auto">Select an enrollment, domain, and at least one unit to continue.</p>}
              <Button
                type="button"
                disabled={!isRequestReady}
                onClick={() => document.getElementById("booking-schedule")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              >
                Request session
              </Button>
            </div>

            {/* Tutor Selection */}
            {assignedTutor && selectedBatch && selectedUnit && (
              <Card>
                <CardHeader>
                  <CardTitle>Tutor Selection</CardTitle>
                  <CardDescription>
                    The system has assigned a tutor based on your selection
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <FormField
                    control={form.control}
                    name="tutor"
                    render={({ field }) => (
                      <FormItem>
                        <div className="space-y-4">
                          <RadioGroup
                            onValueChange={(value) => {
                              handleTutorChange(value);
                            }}
                            value={field.value}
                            className="space-y-4"
                          >
                            {/* Assigned Tutor */}
                            <div className="flex items-start space-x-4 border rounded-lg p-4 bg-green-50 border-green-200">
                              <RadioGroupItem value={assignedTutor.id} id={`tutor-${assignedTutor.id}`} className="mt-1" />
                              <div className="flex-1">
                                <Label htmlFor={`tutor-${assignedTutor.id}`} className="flex items-center">
                                  <div className="flex items-center">
                                    <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                                      {assignedTutor.photoUrl ? (
                                        <img src={assignedTutor.photoUrl} alt={assignedTutor.name} className="w-full h-full object-cover" />
                                      ) : (
                                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                          <User className="h-6 w-6 text-gray-500" />
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <div className="font-medium">{assignedTutor.name}</div>
                                      <div className="text-sm text-gray-500 flex items-center">
                                        <Badge className="mr-2 bg-green-100 text-green-800 hover:bg-green-100">Assigned</Badge>
                                        <span>Rating: {assignedTutor.rating}/5</span>
                                      </div>
                                    </div>
                                  </div>
                                </Label>
                                <div className="mt-2 text-sm text-gray-600">
                                  <div>Specialties: {assignedTutor.specialties.join(", ")}</div>
                                </div>
                              </div>
                            </div>

                            {/* Other Tutors - Only shown when Change Tutor is clicked */}
                            {showChangeTutor && tutors
                              .filter((tutor) => tutor.id !== assignedTutor.id)
                              .map((tutor) => (
                                <div key={tutor.id} className="flex items-start space-x-4 border rounded-lg p-4">
                                  <RadioGroupItem value={tutor.id} id={`tutor-${tutor.id}`} className="mt-1" />
                                  <div className="flex-1">
                                    <Label htmlFor={`tutor-${tutor.id}`} className="flex items-center">
                                      <div className="flex items-center">
                                        <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                                          {tutor.photoUrl ? (
                                            <img src={tutor.photoUrl} alt={tutor.name} className="w-full h-full object-cover" />
                                          ) : (
                                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                                              <User className="h-6 w-6 text-gray-500" />
                                            </div>
                                          )}
                                        </div>
                                        <div>
                                          <div className="font-medium">{tutor.name}</div>
                                          <div className="text-sm text-gray-500 flex items-center">
                                            {tutor.isRequested && (
                                              <Badge className="mr-2 bg-orange-100 text-orange-800 hover:bg-orange-100">Requested</Badge>
                                            )}
                                            <span>Rating: {tutor.rating}/5</span>
                                          </div>
                                        </div>
                                      </div>
                                    </Label>
                                    <div className="mt-2 text-sm text-gray-600">
                                      <div>Specialties: {tutor.specialties.join(", ")}</div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                          </RadioGroup>
                        </div>
                        <FormMessage />

                        {/* Change Tutor Button */}
                        {!showChangeTutor ? (
                          <Button
                            type="button"
                            variant="outline"
                            className="mt-4"
                            onClick={() => loadAlternativeTutors()}
                          >
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Change Tutor
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            className="mt-4"
                            onClick={() => setShowChangeTutor(false)}
                          >
                            <X className="mr-2 h-4 w-4" />
                            Hide Alternative Tutors
                          </Button>
                        )}
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            )}

            {/* Tutor Availability Viewer - Only shown when tutor is selected */}
            {selectedBatch && selectedUnit && assignedTutor && selectedTutor && !tutorChangeRequestSubmitted && (
              <div id="booking-schedule">
                <TutorAvailabilityViewer
                tutorId={selectedTutor}
                studentId={user?.id || "student-001"}
                batchId={selectedBatch}
                curriculumUnitId={selectedUnit}
                curriculumObjectiveId={selectedObjective}
                onRequestSent={() => {
                  toast({
                    title: "Request sent",
                    description: "Session request has been sent successfully.",
                  });

                  // Navigate back to the learning journey page
                  setTimeout(() => {
                    navigate("/student/journey");
                  }, 1500);
                }}
                />
              </div>
            )}

            {/* Tutor Change Request Notice */}
            {tutorChangeRequestSubmitted ? (
              <Card>
                <CardHeader>
                  <CardTitle>Tutor Change Request Submitted</CardTitle>
                  <CardDescription>
                    Your request is being processed
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                    <div className="flex items-start">
                      <Info className="h-6 w-6 text-blue-500 mr-3 mt-0.5" />
                      <div>
                        <h3 className="font-medium text-blue-900 mb-2">Next Steps</h3>
                        <p className="text-blue-800 mb-3">
                          Your tutor change request has been sent to the admin for assessment and approval.
                        </p>
                        <p className="text-blue-800 mb-3">
                          Once approved, you will be notified and can book a session with your requested tutor.
                        </p>
                        <p className="text-blue-800">
                          You will receive a notification when a decision has been made.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="flex justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate("/student/journey")}
                  >
                    Return to Learning Journey
                  </Button>
                </CardFooter>
              </Card>
            ) : null}
          </form>
        </Form>
      )}

      {/* Tutor Change Reason Dialog */}
      <Dialog open={showTutorChangeDialog} onOpenChange={setShowTutorChangeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reason for Tutor Change</DialogTitle>
            <DialogDescription>
              Please provide a reason for requesting a different tutor. This information will be sent to the admin.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={tutorChangeReason}
            onChange={(e) => setTutorChangeReason(e.target.value)}
            placeholder="Enter your reason here..."
            className="min-h-[100px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowTutorChangeDialog(false);
              setSelectedTutor(assignedTutor?.id || null);
              form.setValue("tutor", assignedTutor?.id || "");
            }}>
              Cancel
            </Button>
            <Button onClick={confirmTutorChange} disabled={!tutorChangeReason.trim()}>
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RequestBooking;
